import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import { Component, Input, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { GeneratedCommandFormComponent } from '@app/shared/forms/generated-command-form.component';
import { createWorkflowReviewFormTerminology } from '@app/shared/terminology/workflow-review-form-terminology';
import { WORKFLOW_DIAGNOSTICS_REVIEW_TERMINOLOGY } from '@app/shared/terminology/workflow-review-form.terminology';
import { WorkflowReviewRequestForm } from '@app/generated/contracts/workflow-review-request.generated';
import { WorkflowReviewSubmissionRequestForm } from '@app/generated/contracts/workflow-review-submission-request.generated';
import {
  editableFormPayload,
  validateGeneratedForm,
  serverFormIssues,
  FormIssues,
  GeneratedFormValues,
  GeneratedFormDefinition,
} from '@template-ui/helpers/generated-form.helpers';
import { AppApis } from '@app/core/APIs/app-apis';
import type { WorkflowDefinitionVersionDetail } from './platform-workflow-registry-diagnostics.models';
import { applyWorkflowFormConfiguration, type WorkflowFormConfiguration } from '@app/shared/workflow-commands/workflow-form-configuration';
import { isWorkflowFormVersionAvailable } from '@app/shared/workflow-commands/workflow-form-layout';
import {
  applyRuntimeFieldPolicy,
  policyAllowsOutcomes,
  fieldPolicyKey,
  policyEditableValues,
  policyInitialValues,
  type WorkflowRuntimeFieldPolicy,
  type WorkflowFieldPolicySource,
} from '@app/shared/workflow-commands/workflow-runtime-field-policy';
import type { WorkflowClientCommandReference } from '@app/shared/workflow-commands/workflow-command-registry';
import { reviewHistoryNotes, reviewHistoryWorkSummary, type WorkflowReviewHistoryRow } from '@app/shared/workflow-commands/workflow-review-history';

export interface WorkflowAvailableAction {
  command: {
    key: { code: string; provider?: string; kind?: string; version?: number };
    inputSchemaHash: string;
    outputSchemaHash?: string;
    contractHash?: string;
  };
  enabled: boolean;
  form: {
    contract: string;
    version: number;
    inputSchemaHash: string;
    configuration?:
      | import('@app/shared/workflow-commands/workflow-form-configuration').WorkflowFormConfiguration
      | null;
    captureForm?:
      | import('@app/shared/workflow-commands/workflow-capture-form').WorkflowCaptureForm
      | null;
    fieldPolicy?: WorkflowRuntimeFieldPolicy | null;
    initialValues?: Record<string, unknown> | null;
    readOnlyFieldIds?: string[] | null;
    lookupFields?: import('@app/shared/workflow-commands/workflow-form-lookup').WorkflowFormLookupFields | null;
  } | null;
  blockReasons: { code: string; message: string }[];
  outcomes: {
    code: string;
    label: string;
    enabled: boolean;
    reasonPolicy: string;
    reasonCapture?:
      | import('@app/shared/workflow-commands/workflow-reason-capture').WorkflowReasonCaptureContract
      | null;
    blockReasons: { code: string; message: string }[];
  }[];
}
export interface WorkflowExecutionState {
  availableActions?: {
    occurrenceId: number | null;
    stepETag: string | null;
    blockReasons: { code: string; message: string }[];
    actions: WorkflowAvailableAction[];
  } | null;
  workflowInstanceId: number;
  workflowDefinitionVersionId?: number;
  publicReference?: string | null;
  assignment?: {
    currentQueueCode: string | null;
    currentAssigneeUserId: number | null;
    policyId: string | null;
    mode: string | null;
    eligibleQueues: string[] | null;
    assignmentBasisCode?: 'roles' | null;
    eligibleRoleCodes?: string[] | null;
  } | null;
  workflowCode: string;
  definitionVersionCode: string;
  canonicalContentHash: string;
  configurationVersionId: number;
  statusCode: string;
  currentStepCode: string | null;
  graphExecutionEnabled: boolean;
  activeOccurrenceId?: number;
  reviewRequired?: boolean;
  review?: {
    id: number;
    statusCode: string;
    requestedByUserId: number;
    notes: string;
  } | null;
  canSubmitReview?: boolean;
  reviewIssue?: string | null;
  reviewHistory?: WorkflowReviewHistoryRow[];
  eTag: string;
  stepETag: string | null;
  occurrences: {
    id: number;
    definitionNodeId: string;
    stepCode: string;
    occurrenceNumber: number;
    statusCode: string;
  }[];
}

@Component({
  selector: 'phms-workflow-execution-panel',
  standalone: true,
  imports: [
    UiButtonDirective,
    UiControlDirective,
    CommonModule,
    FormsModule,
    GeneratedCommandFormComponent,
  ],
  template: ` <section aria-labelledby="execution-title">
    <h3 id="execution-title">Live execution tools</h3>
    <p>
      Creates a real process run. The server selects the active version for this
      workflow and scope.
    </p>
    <phms-generated-command-form
      *ngIf="supportsProcessData && startingForm.fields.length"
      [definition]="startingForm"
      [values]="startingValues"
      (valuesChange)="startingValues = $event; startingIssues = {}"
      [issues]="startingIssues"
      [disabled]="busy() || created() || !!state()"
    />
    <div class="execution-controls">
      <label *ngIf="requiresSubject"
        >Record ID
        <input
          uiControl
          type="number"
          min="1"
          [(ngModel)]="subjectId"
          [disabled]="busy()"
      /></label>
      <button
        uiButton
        severity="primary"
        [outlined]="false"
        type="button"
        (click)="create()"
        [disabled]="busy() || !canCreate() || created() || !!state()"
      >
        Create process run
      </button>
    </div>
    <div class="execution-controls">
      <label
        >Workflow ID
        <input
          uiControl
          type="number"
          min="1"
          [(ngModel)]="instanceId"
          [disabled]="busy()"
      /></label>
      <button
        uiButton
        type="button"
        (click)="load()"
        [disabled]="busy() || !instanceId"
      >
        Load / refresh
      </button>
    </div>
    <p *ngIf="error()" role="alert">{{ error() }}</p>
    <ng-container *ngIf="state() as run">
      <button uiButton type="button" (click)="newRun()" [disabled]="busy()">
        Set up another workflow
      </button>
      <a [href]="'/workflows/' + run.workflowInstanceId"
        >Open staff workspace</a
      >
      <p>Public reference: {{ run.publicReference || 'Not assigned' }}</p>
      <p role="status">
        Internal workflow {{ run.workflowInstanceId }} · {{ run.statusCode }} ·
        Current step: {{ run.currentStepCode || 'none' }}
      </p>
      <p>
        Version {{ run.definitionVersionCode }} · Configuration
        {{ run.configurationVersionId }}
      </p>
      <p *ngIf="run.definitionVersionCode !== viewedVersionCode">
        This run uses a different version from the page above.
        <a [href]="runVersionUrl(run)">Open this run’s published version</a>.
      </p>
      <details>
        <summary>Published content hash</summary>
        <code>{{ run.canonicalContentHash }}</code>
      </details>
      <p *ngIf="!run.graphExecutionEnabled">
        Execution is disabled by the server. The saved version remains pinned.
      </p>
      <label *ngIf="run.statusCode !== 'completed' && !run.reviewRequired"
        >Reason
        <input
          uiControl
          type="text"
          maxlength="120"
          [(ngModel)]="reason"
          [disabled]="busy()"
      /></label>
      <button
        uiButton
        *ngIf="run.statusCode === 'not_started'"
        type="button"
        (click)="advance()"
        [disabled]="busy() || !run.graphExecutionEnabled"
      >
        Start workflow
      </button>
      <button
        uiButton
        *ngIf="run.statusCode === 'in_progress' && !run.reviewRequired"
        type="button"
        (click)="advance()"
        [disabled]="
          busy() || !run.graphExecutionEnabled || !run.currentStepCode
        "
      >
        Complete current step
      </button>
      <section *ngIf="run.reviewRequired" aria-label="Independent review">
        <h4>Independent review</h4>
        <p *ngFor="let message of availabilityMessages()" role="status">
          {{ message }}
        </p>
        <p *ngIf="run.reviewIssue" role="alert">{{ run.reviewIssue }}</p>
        <ng-container *ngIf="!run.review && !run.reviewIssue">
          <phms-generated-command-form
            [definition]="reviewTerminology.project(requestForm).definition"
            [terminologyControls]="reviewTerminology.project(requestForm).controls"
            [values]="requestValues"
            (valuesChange)="updateRequestValues($event)"
            [issues]="requestIssues"
            [disabled]="busy() || !reviewActionEnabled(false)"
          />
          <button
            uiButton
            type="button"
            (click)="requestReview()"
            [disabled]="busy() || !reviewActionEnabled(false)"
          >
            Request independent review
          </button>
        </ng-container>
        <ng-container *ngIf="run.review as review">
          <p>
            Submitted by user {{ review.requestedByUserId }}: {{ review.notes }}
          </p>
          <phms-generated-command-form
            [definition]="reviewTerminology.project(submissionForm).definition"
            [terminologyControls]="reviewTerminology.project(submissionForm).controls"
            [values]="submissionValues"
            (valuesChange)="updateSubmissionValues($event)"
            [issues]="submissionIssues"
            [disabled]="busy() || !reviewAction(true)?.enabled"
          />
          <button
            uiButton
            type="button"
            (click)="submitReview()"
            [disabled]="busy() || !reviewActionEnabled(true)"
          >
            Submit review and continue
          </button>
        </ng-container>
      </section>
      <section
        *ngIf="run.assignment?.policyId"
        aria-label="Current step assignment"
      >
        <h4>Current step assignment</h4>
        <p>
          <ng-container *ngIf="run.assignment?.assignmentBasisCode !== 'roles'"
            >Queue:
            {{ run.assignment?.currentQueueCode || 'Unassigned' }}
            · </ng-container
          >Staff user ID:
          {{ run.assignment?.currentAssigneeUserId || 'Unclaimed' }}
        </p>
        <p *ngIf="run.assignment?.assignmentBasisCode === 'roles'">
          Assign staff from this step's dashboard. Eligible assignment roles:
          {{ run.assignment?.eligibleRoleCodes?.join(', ') }}.
        </p>
        <div
          class="execution-controls"
          *ngIf="run.assignment?.assignmentBasisCode !== 'roles'"
        >
          <label
            >Receiving queue
            <select
              uiControl
              [(ngModel)]="assignmentQueue"
              [disabled]="busy() || run.statusCode !== 'in_progress'"
            >
              <option value="">Current queue</option>
              <option
                *ngFor="let queue of run.assignment?.eligibleQueues"
                [value]="queue"
              >
                {{ queue }}
              </option>
            </select></label
          >
          <label
            >Staff user ID
            <input
              uiControl
              type="number"
              min="1"
              [(ngModel)]="assignmentUser"
              [disabled]="busy() || !allowsDirect(run)"
          /></label>
          <button
            uiButton
            type="button"
            (click)="assign('claim')"
            [disabled]="
              busy() ||
              run.statusCode !== 'in_progress' ||
              !!run.assignment?.currentAssigneeUserId ||
              !allowsClaim(run)
            "
          >
            Claim
          </button>
          <button
            uiButton
            type="button"
            (click)="assign('reassign')"
            [disabled]="
              busy() ||
              run.statusCode !== 'in_progress' ||
              !assignmentUser ||
              !allowsDirect(run)
            "
          >
            Assign staff
          </button>
          <button
            uiButton
            type="button"
            (click)="assign('queue')"
            [disabled]="
              busy() || run.statusCode !== 'in_progress' || !assignmentQueue
            "
          >
            Move to queue
          </button>
          <button
            uiButton
            type="button"
            (click)="assign('unclaim')"
            [disabled]="
              busy() ||
              run.statusCode !== 'in_progress' ||
              !run.assignment?.currentAssigneeUserId
            "
          >
            Release to queue
          </button>
        </div>
      </section>
      <table>
        <caption>
          Work created on this path
        </caption>
        <thead>
          <tr>
            <th>Step</th>
            <th>Occurrence</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let step of run.occurrences">
            <td>{{ step.stepCode }}</td>
            <td>{{ step.occurrenceNumber }}</td>
            <td>{{ step.statusCode }}</td>
          </tr>
        </tbody>
      </table>
      <table *ngIf="run.reviewHistory?.length">
        <caption>
          Review history
        </caption>
        <thead>
          <tr>
            <th>Step</th>
            <th>Occurrence</th>
            <th>Result</th>
            <th>Notes</th>
            <th *ngIf="hasSubmittedWork(run.reviewHistory)">Submitted work</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let review of run.reviewHistory">
            <td>{{ review.stepCode }}</td>
            <td>{{ review.occurrenceNumber }}</td>
            <td>
              {{
                review.statusCode === 'returned'
                  ? 'Changes required'
                  : review.statusCode === 'approved'
                    ? 'Approved'
                    : 'Pending'
              }}
            </td>
            <td>{{ reviewHistoryNotes(review) }}</td>
            <td *ngIf="hasSubmittedWork(run.reviewHistory)">{{ reviewHistoryWorkSummary(review) || '—' }}</td>
          </tr>
        </tbody>
      </table>
    </ng-container>
  </section>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
        max-width: 100%;
        color: var(--custom-text-secondary-color);
      }
      section {
        min-width: 0;
        display: grid;
        gap: 0.75rem;
        padding: 1rem;
        border-bottom: 1px solid var(--custom-border-subtle-color);
      }
      h3,
      p {
        margin: 0;
        overflow-wrap: anywhere;
      }
      .execution-controls {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        align-items: center;
      }
      label {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }
      input {
        width: 12rem;
        max-width: 100%;
        box-sizing: border-box;
      }
      textarea,
      select {
        max-width: 100%;
        box-sizing: border-box;
      }
      textarea {
        width: 28rem;
        resize: vertical;
      }
      button {
        width: fit-content;
      }
      a {
        color: var(--custom-hyperlink-text-color);
      }
      a:hover {
        color: var(--custom-hyperlink-text-hover-color);
      }
      [role='alert'] {
        color: var(--custom-text-color-error);
      }
      table {
        width: 100%;
        border-collapse: collapse;
      }
      caption {
        text-align: left;
        font-weight: 600;
      }
      @media (max-width: 600px) {
        section {
          padding: 0.5rem;
        }
        section section {
          padding: 0.25rem 0;
        }
      }
      th,
      td {
        overflow-wrap: anywhere;
        text-align: left;
        padding: 0.4rem;
        border-bottom: 1px solid var(--custom-border-subtle-color);
      }
      code {
        overflow-wrap: anywhere;
      }
    `,
  ],
})
export class WorkflowExecutionPanelComponent {
  readonly reviewHistoryNotes = reviewHistoryNotes;
  readonly reviewHistoryWorkSummary = reviewHistoryWorkSummary;
  readonly hasSubmittedWork = (reviews?: WorkflowReviewHistoryRow[]) => reviews?.some(review => reviewHistoryWorkSummary(review) !== null) ?? false;
  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);
  private currentDefinition!: WorkflowDefinitionVersionDetail;
  @Input({ required: true }) set definition(
    value: WorkflowDefinitionVersionDetail,
  ) {
    if (this.currentDefinition?.id !== value.id) {
      this.state.set(null);
      this.instanceId = null;
      this.subjectId = null;
      this.reason = '';
      this.startingValues = {};
      this.startingIssues = {};
      this.requestIssues = {};
      this.submissionIssues = {};
      this.pendingIntent = null;
      this.assignmentQueue = '';
      this.assignmentUser = null;
      this.workSummary = '';
      this.reviewNotes = '';
      this.reviewOutcome = 'approved';
      this.error.set(null);
      this.created.set(false);
    }
    this.currentDefinition = value;
  }
  readonly state = signal<WorkflowExecutionState | null>(null);
  readonly busy = signal(false);
  readonly created = signal(false);
  readonly error = signal<string | null>(null);
  subjectId: number | null = null;
  startingValues: GeneratedFormValues = {};
  startingIssues: FormIssues = {};
  get supportsProcessData(): boolean {
    return [
      '1.10',
      '1.11',
      '1.12',
      '1.13',
      '1.14',
      '1.15',
      '1.16',
      '1.17', '1.18', '1.19', '1.20', '1.21',
    ].includes(
      this.currentDefinition?.definitionDocument?.payloadSchemaVersion ?? '',
    );
  }
  get requiresSubject(): boolean {
    const doc = this.currentDefinition?.definitionDocument;
    return (
      ![
        '1.10',
        '1.11',
        '1.12',
        '1.13',
        '1.14',
        '1.15',
        '1.16',
        '1.17', '1.18', '1.19', '1.20', '1.21',
      ].includes(doc?.payloadSchemaVersion ?? '') || !!doc?.subjectTypeCode
    );
  }
  get startingForm(): GeneratedFormDefinition {
    return {
      contract: 'WorkflowProcessStart',
      version: 1,
      fields: (
        this.currentDefinition?.definitionDocument?.processData?.fields ?? []
      ).map((field) => ({
        name: field.key,
        label: field.label,
        labelToken: '',
        required: !!field.requiredAtStart,
        nullable: !field.requiredAtStart,
        type:
          field.type === 'record-reference' || field.type === 'date'
            ? 'string'
            : field.type,
        control:
          field.type === 'boolean'
            ? 'checkbox'
            : ['number', 'integer'].includes(field.type)
              ? 'number'
              : field.type === 'date'
                ? 'date'
                : 'text',
        maxLength:
          field.type === 'record-reference'
            ? 160
            : field.type === 'string'
              ? 100000
              : undefined,
        minimum: field.type === 'integer' ? -2147483648 : undefined,
        maximum: field.type === 'integer' ? 2147483647 : undefined,
        dateSemantics: field.type === 'date' ? 'calendar-date' : undefined,
        errorCodes: [],
      })),
    };
  }
  canCreate(): boolean {
    return (
      (!this.requiresSubject ||
        (Number.isInteger(this.subjectId) && this.subjectId! > 0)) &&
      (!this.supportsProcessData ||
        Object.keys(
          validateGeneratedForm(this.startingForm, this.startingValues),
        ).length === 0)
    );
  }
  private startingData(): Record<string, unknown> {
    const values = editableFormPayload(this.startingForm, this.startingValues);
    return Object.fromEntries(
      (this.currentDefinition.definitionDocument?.processData?.fields ?? [])
        .filter(
          (field) =>
            Object.hasOwn(values, field.key) &&
            values[field.key] != null &&
            values[field.key] !== '',
        )
        .map((field) => [
          field.key,
          field.type === 'record-reference'
            ? {
                recordTypeCode: field.recordTypeCode,
                id: String(values[field.key]).trim(),
              }
            : values[field.key],
        ]),
    );
  }
  instanceId: number | null = null;
  reason = '';
  private applyActionFieldPolicy(
    form: GeneratedFormDefinition | null,
    action?: WorkflowAvailableAction,
  ): GeneratedFormDefinition | null {
    if (!form) return null;
    const run = this.state();
    const source: WorkflowFieldPolicySource | undefined =
      run?.activeOccurrenceId && run.workflowDefinitionVersionId
        ? {
            workflowInstanceId: run.workflowInstanceId,
            occurrenceId: run.activeOccurrenceId,
            configurationVersionId: run.configurationVersionId,
            definitionVersionId: run.workflowDefinitionVersionId,
            definitionHash: run.canonicalContentHash,
          }
        : undefined;
    if (action?.form?.fieldPolicy && !source) return null;
    if (!action) return form;
    const resolved = applyRuntimeFieldPolicy(
      form,
      action.form?.fieldPolicy,
      action.command as WorkflowClientCommandReference,
      source,
      action.outcomes
        .filter((outcome) => !outcome.enabled)
        .map((outcome) => outcome.code),
    );
    return resolved &&
      policyAllowsOutcomes(action.form?.fieldPolicy, action.outcomes)
      ? resolved
      : null;
  }
  private formVersionAvailable(configuration: WorkflowFormConfiguration | null | undefined): boolean {
    if (!isWorkflowFormVersionAvailable(this.currentDefinition?.definitionDocument?.payloadSchemaVersion, configuration)) return false;
    return configuration?.version !== 2 || this.state()?.definitionVersionCode === this.currentDefinition?.definitionVersionCode;
  }
  private readonly resolvedRequestForm = computed(() => {
    const action = this.reviewAction(false);
    if (!this.formVersionAvailable(action?.form?.configuration)) return null;
    return this.applyActionFieldPolicy(
      applyWorkflowFormConfiguration(
        WorkflowReviewRequestForm,
        action?.form?.configuration,
      ),
      action,
    );
  });

  get requestForm(): GeneratedFormDefinition {
    return (
      this.resolvedRequestForm() ?? { ...WorkflowReviewRequestForm, fields: [] }
    );
  }
  private readonly resolvedSubmissionForm = computed<GeneratedFormDefinition>(
    () => {
      const action = this.reviewAction(true),
        outcomes = action?.outcomes;
      if (!this.formVersionAvailable(action?.form?.configuration))
        return { ...WorkflowReviewSubmissionRequestForm, fields: [] };
      const configured = applyWorkflowFormConfiguration(
        WorkflowReviewSubmissionRequestForm,
        action?.form?.configuration,
        { outcomeField: 'outcome', outcomes },
      );
      if (!configured)
        return { ...WorkflowReviewSubmissionRequestForm, fields: [] };
      if (
        !outcomes ||
        action?.form?.configuration?.fields.some(
          (field) => field.name === 'outcome',
        )
      )
        return (
          this.applyActionFieldPolicy(configured, action) ?? {
            ...configured,
            fields: [],
          }
        );
      const resolved = {
        ...configured,
        fields: configured.fields.map((field) =>
          field.name === 'outcome'
            ? {
                ...field,
                options: outcomes.map((o) => ({
                  value: o.code,
                  label: o.label,
                  labelToken: '',
                })),
              }
            : field,
        ),
      };
      return (
        this.applyActionFieldPolicy(resolved, action) ?? {
          ...configured,
          fields: [],
        }
      );
    },
  );
  get submissionForm(): GeneratedFormDefinition {
    return this.resolvedSubmissionForm();
  }
  readonly reviewTerminology = createWorkflowReviewFormTerminology(WORKFLOW_DIAGNOSTICS_REVIEW_TERMINOLOGY);
  private readonly reviewPolicyKeys = new Map<boolean, string | null>();
  private safePolicyValues(
    submission: boolean,
    values: GeneratedFormValues,
  ): GeneratedFormValues {
    const action = this.reviewAction(submission);
    if (!action?.form?.fieldPolicy) return values;
    const form = submission ? this.submissionForm : this.requestForm;
    return {
      ...policyInitialValues(
        form,
        action.form.fieldPolicy,
        action.form.initialValues ?? {},
      ),
      ...policyEditableValues(form, values),
    };
  }
  updateRequestValues(values: GeneratedFormValues): void {
    this.requestValues = this.safePolicyValues(false, values);
    this.requestIssues = {};
  }
  updateSubmissionValues(values: GeneratedFormValues): void {
    this.submissionValues = this.safePolicyValues(true, values);
    this.submissionIssues = {};
  }
  private commandFormPayload(submission: boolean): Record<string, unknown> {
    const action = this.reviewAction(submission),
      form = submission ? this.submissionForm : this.requestForm;
    const values = submission ? this.submissionValues : this.requestValues;
    return editableFormPayload(
      form,
      action?.form?.fieldPolicy ? policyEditableValues(form, values) : values,
    );
  }
  requestValues: GeneratedFormValues = { workSummary: '' };
  submissionValues: GeneratedFormValues = { outcome: 'approved', notes: '' };
  requestIssues: FormIssues = {};
  submissionIssues: FormIssues = {};
  get workSummary(): string {
    return String(this.requestValues['workSummary'] ?? '');
  }
  set workSummary(value: string) {
    this.requestValues = { ...this.requestValues, workSummary: value };
  }
  get reviewNotes(): string {
    return String(this.submissionValues['notes'] ?? '');
  }
  set reviewNotes(value: string) {
    this.submissionValues = { ...this.submissionValues, notes: value };
  }
  get reviewOutcome(): string {
    return String(this.submissionValues['outcome'] ?? '');
  }
  set reviewOutcome(value: string) {
    this.submissionValues = { ...this.submissionValues, outcome: value };
  }
  assignmentQueue = '';
  assignmentUser: number | null = null;
  private pendingIntent: { signature: string; key: string } | null = null;

  newRun(): void {
    if (this.busy()) return;
    this.state.set(null);
    this.created.set(false);
    this.instanceId = null;
    this.subjectId = null;
    this.startingValues = {};
    this.startingIssues = {};
    this.requestIssues = {};
    this.submissionIssues = {};
    this.reason = '';
    this.assignmentQueue = '';
    this.assignmentUser = null;
    this.workSummary = '';
    this.reviewNotes = '';
    this.reviewOutcome = 'approved';
    this.pendingIntent = null;
    this.error.set(null);
  }

  async create(): Promise<void> {
    if (this.busy() || this.created() || this.state()) return;
    this.startingIssues = validateGeneratedForm(
      this.startingForm,
      this.startingValues,
    );
    if (!this.canCreate()) return;
    const doc = this.currentDefinition.definitionDocument;
    if (!doc) return;
    await this.perform(async () => {
      const body = {
        workflowCode: doc.workflowCode,
        subjectId: this.requiresSubject ? this.subjectId : null,
        scopeTypeCode: doc.scopeTypeCode,
        scopeKey: doc.scopeKey,
        ...(this.supportsProcessData
          ? { startingData: this.startingData() }
          : {}),
      };
      const result = await this.post<{ workflowInstanceId: number }>(
        this.apis.core.manual.platformWorkflowExecution(),
        body,
      );
      this.instanceId = result.workflowInstanceId;
      this.created.set(true);
      await this.refresh();
    });
  }
  async load(): Promise<void> {
    if (
      this.busy() ||
      !Number.isInteger(this.instanceId) ||
      this.instanceId! < 1
    )
      return;
    if (this.state()?.workflowInstanceId !== this.instanceId) {
      this.requestValues = { workSummary: '' };
      this.submissionValues = { outcome: 'approved', notes: '' };
      this.requestIssues = {};
      this.submissionIssues = {};
      this.pendingIntent = null;
    }
    this.state.set(null);
    await this.perform(() => this.refresh());
  }
  async advance(): Promise<void> {
    const run = this.state();
    if (this.busy() || !run?.graphExecutionEnabled || run.reviewRequired)
      return;
    this.instanceId = run.workflowInstanceId;
    const starting = run.statusCode === 'not_started';
    if (!starting && !run.currentStepCode) return;
    await this.perform(async () => {
      const suffix = starting
        ? '/transition'
        : `/steps/${encodeURIComponent(run.currentStepCode!)}/transition`;
      await this.post(
        this.apis.core.manual.platformWorkflowExecution(
          run.workflowInstanceId,
          suffix,
        ),
        {
          targetStateCode: starting ? 'in_progress' : 'completed',
          reasonCode: this.reason.trim() || null,
        },
        starting ? run.eTag : run.stepETag!,
      );
      await this.refresh();
      this.reason = '';
    });
  }
  async requestReview(): Promise<void> {
    const run = this.state();
    if (this.busy() || !run || !this.reviewActionEnabled(false)) return;
    this.requestIssues = validateGeneratedForm(
      this.requestForm,
      this.requestValues,
    );
    if (Object.keys(this.requestIssues).length) return;
    await this.perform(async () => {
      await this.post(
        this.apis.core.manual.platformWorkflowExecution(
          run.workflowInstanceId,
          '/review/request',
        ),
        {
          ...this.commandFormPayload(false),
          occurrenceId: run.activeOccurrenceId,
          expectedETag: run.stepETag,
        },
      );
      await this.refresh();
      this.workSummary = '';
    });
  }
  async submitReview(): Promise<void> {
    const run = this.state();
    if (this.busy() || !run?.review || !this.reviewActionEnabled(true)) return;
    this.submissionIssues = validateGeneratedForm(
      this.submissionForm,
      this.submissionValues,
    );
    if (
      this.reviewAction(true)?.outcomes.find(
        (o) => o.code === this.reviewOutcome,
      )?.reasonPolicy === 'required' &&
      !this.reviewNotes.trim()
    )
      this.submissionIssues = {
        ...this.submissionIssues,
        notes: 'A reason is required for this outcome.',
      };
    if (Object.keys(this.submissionIssues).length) return;
    await this.perform(async () => {
      await this.post(
        this.apis.core.manual.platformWorkflowExecution(
          run.workflowInstanceId,
          '/review',
        ),
        {
          ...this.commandFormPayload(true),
          occurrenceId: run.activeOccurrenceId,
          decisionRecordId: run.review!.id,
          expectedETag: run.stepETag,
        },
      );
      await this.refresh();
      this.reviewNotes = '';
      this.reviewOutcome = 'approved';
    });
  }
  reviewAction(submission: boolean): WorkflowAvailableAction | undefined {
    const run = this.state();
    const available = run?.availableActions;
    if (
      !run?.graphExecutionEnabled ||
      !available ||
      available.occurrenceId !== run.activeOccurrenceId ||
      available.stepETag !== run.stepETag
    )
      return undefined;
    const action = available.actions.find(
      (a) =>
        a.command.key.code ===
        (submission ? 'workflow.review.submit' : 'workflow.review.request'),
    );
    const form = submission
      ? WorkflowReviewSubmissionRequestForm
      : WorkflowReviewRequestForm;
    return action?.form?.contract === form.contract &&
      action.form.version === form.version &&
      action.form.inputSchemaHash === action.command.inputSchemaHash
      ? action
      : undefined;
  }
  reviewActionEnabled(submission: boolean): boolean {
    const action = this.reviewAction(submission);
    return (
      !!action?.enabled &&
      (submission
        ? this.submissionForm.fields.length > 0
        : !!this.resolvedRequestForm()) &&
      (!submission ||
        !!action.outcomes.find((o) => o.code === this.reviewOutcome)?.enabled)
    );
  }
  availabilityMessages(): string[] {
    const run = this.state();
    const available = run?.availableActions;
    if (!available)
      return [
        'Current review actions are unavailable. Refresh before continuing.',
      ];
    const action = this.reviewAction(!!run?.review);
    const messages = [
      ...available.blockReasons.map((r) => r.message),
      ...(action?.blockReasons.map((r) => r.message) ?? []),
      ...(action?.outcomes
        .find((o) => o.code === this.reviewOutcome)
        ?.blockReasons.map((r) => r.message) ?? []),
    ];
    if (!action && messages.length === 0)
      messages.push(
        'This review command is not available with your current access or retained configuration.',
      );
    return [...new Set(messages)];
  }
  allowsClaim(run: WorkflowExecutionState): boolean {
    return ['claim', 'claim_or_direct'].includes(run.assignment?.mode ?? '');
  }
  allowsDirect(run: WorkflowExecutionState): boolean {
    return ['direct', 'claim_or_direct'].includes(run.assignment?.mode ?? '');
  }
  async assign(
    action: 'claim' | 'reassign' | 'queue' | 'unclaim',
  ): Promise<void> {
    const run = this.state();
    if (
      this.busy() ||
      !run?.assignment?.policyId ||
      run.assignment.assignmentBasisCode === 'roles' ||
      run.statusCode !== 'in_progress' ||
      !run.currentStepCode ||
      !run.stepETag
    )
      return;
    if (
      (action === 'claim' && !this.allowsClaim(run)) ||
      (action === 'reassign' &&
        (!this.allowsDirect(run) ||
          !Number.isInteger(this.assignmentUser) ||
          this.assignmentUser! <= 0))
    )
      return;
    if (action === 'queue' && !this.assignmentQueue) return;
    this.instanceId = run.workflowInstanceId;
    await this.perform(async () => {
      const body =
        action === 'queue'
          ? {
              queueCode: this.assignmentQueue,
              reasonCode: this.reason.trim() || null,
            }
          : action === 'reassign'
            ? {
                assigneeUserId: this.assignmentUser,
                queueCode: this.assignmentQueue || null,
                reasonCode: this.reason.trim() || null,
              }
            : {};
      await this.post(
        this.apis.core.manual.platformWorkflowExecution(
          run.workflowInstanceId,
          '/steps/' +
            encodeURIComponent(run.currentStepCode!) +
            '/assignment/' +
            action,
        ),
        body,
        run.stepETag!,
      );
      await this.refresh();
      this.assignmentUser = null;
      this.assignmentQueue = '';
    });
  }
  get viewedVersionCode(): string {
    return this.currentDefinition.definitionVersionCode;
  }
  runVersionUrl(run: WorkflowExecutionState): string {
    return `/platform-admin/workflow-registry-diagnostics?workflow=${encodeURIComponent(run.workflowCode)}&version=${encodeURIComponent(run.definitionVersionCode)}&registryView=version&versionView=review`;
  }
  private async refresh(): Promise<void> {
    const result = await firstValueFrom(
      this.http.get<WorkflowExecutionState>(
        this.apis.core.manual.platformWorkflowExecution(
          this.instanceId!,
          '/execution',
        ),
        this.options(),
      ),
    );
    if (result.workflowCode !== this.currentDefinition.workflowCode) {
      this.state.set(null);
      throw new Error(
        'This instance belongs to a different workflow. Open that workflow to continue it.',
      );
    }
    this.state.set(result);
    for (const submission of [false, true]) {
      const next = fieldPolicyKey(
        this.reviewAction(submission)?.form?.fieldPolicy,
      );
      const prior = this.reviewPolicyKeys.get(submission) ?? null;
      const changed = prior !== next && (!!prior || !!next);
      const values = changed
        ? {}
        : submission
          ? this.submissionValues
          : this.requestValues;
      if (submission)
        this.submissionValues = this.safePolicyValues(true, values);
      else this.requestValues = this.safePolicyValues(false, values);
      this.reviewPolicyKeys.set(submission, next);
      if (changed) this.pendingIntent = null;
    }
  }
  private async post<T>(url: string, body: object, etag?: string): Promise<T> {
    const signature = JSON.stringify([url, body, etag]);
    if (this.pendingIntent?.signature !== signature)
      this.pendingIntent = { signature, key: crypto.randomUUID() };
    const options = this.options();
    options.headers = options.headers.set(
      'Idempotency-Key',
      this.pendingIntent.key,
    );
    if (etag) options.headers = options.headers.set('If-Match', etag);
    const result = await firstValueFrom(this.http.post<T>(url, body, options));
    this.pendingIntent = null;
    return result;
  }
  private options() {
    return {
      withCredentials: true,
      headers: new HttpHeaders({
        'skip-error-interceptor': 'yes',
        'skip-loading-interceptor': 'yes',
      }),
    };
  }
  private async perform(action: () => Promise<void>): Promise<void> {
    this.busy.set(true);
    this.error.set(null);
    try {
      await action();
    } catch (error) {
      const deniedPolicy =
        error instanceof HttpErrorResponse &&
        [401, 403, 404].includes(error.status) &&
        ([...this.reviewPolicyKeys.values()].some(Boolean) ||
          this.state()?.availableActions?.actions.some(
            (action) => !!action.form?.fieldPolicy,
          ));
      if (deniedPolicy) {
        this.state.set(null);
        this.requestValues = {};
        this.submissionValues = {};
        this.requestIssues = {};
        this.submissionIssues = {};
        this.pendingIntent = null;
        this.reviewPolicyKeys.clear();
      } else if (error instanceof HttpErrorResponse) {
        this.requestIssues = serverFormIssues(
          this.requestForm,
          error.error ?? {},
        );
        this.submissionIssues = serverFormIssues(
          this.submissionForm,
          error.error ?? {},
        );
      }
      // Ordinary validation/concurrency rejection keeps input and retry identity; lost policy access clears it.
      this.error.set(
        error instanceof HttpErrorResponse
          ? error.error?.detail ||
              error.error?.message ||
              error.error?.Detail ||
              error.error?.Message ||
              'The command was not confirmed. Refresh the workflow before retrying.'
          : error instanceof Error
            ? error.message
            : 'Unable to execute workflow.',
      );
    } finally {
      this.busy.set(false);
    }
  }
}
